import { CreditCardIcon, UserIcon } from '@heroicons/react/24/outline'
import { QSE_ENTERPRISE_ID, User, UserRoleEnum } from '@qse/constants'
import { useBillingStatusStore, useUserStore } from '@qse/stores'
import {
  Navbar,
  PlusIcon,
  QuotaAdjustmentBanner,
  Sidebar,
  StorageIcon,
  UploadIcon,
} from '@qse/ui'
import { getDashboardUrl } from '@qse/utils'
import { type FC, type ReactNode } from 'react'
import { Outlet } from 'react-router-dom'

import { useBillingStatus } from '@/hooks'
import { AppRoutesList } from '@/router/routes'

interface DashboardLayoutProps {
  children?: ReactNode
}

/** Tenant-Admin portal shell — TENANT_ADMIN navigation only. */
const DashboardLayout: FC<DashboardLayoutProps> = () => {
  const { user } = useUserStore()
  const { showQuotaAdjustmentBanner, quotaAdjustmentGracePeriodStatus } = useBillingStatusStore()

  useBillingStatus()

  const getRedirectUrl = () => getDashboardUrl('/')

  const userType = user?.userRole || ''
  const isTenantAdmin = userType === UserRoleEnum.TENANT_ADMIN

  const shouldShowQuotaAdjustmentBanner =
    isTenantAdmin &&
    showQuotaAdjustmentBanner &&
    !!quotaAdjustmentGracePeriodStatus?.hasActiveGracePeriod &&
    !!quotaAdjustmentGracePeriodStatus?.targetPlan

  const adminMenuItems = [
    {
      id: 'dashboard',
      label: 'Go to Drive',
      icon: PlusIcon,
      isPrimary: true,
      hasArrow: true,
      externalIcon: UploadIcon,
      redirectUrl: getRedirectUrl(),
      isExternal: true,
    },
    {
      id: 'users',
      label: 'Manage Storage',
      icon: StorageIcon,
      redirectUrl: AppRoutesList.users,
    },
    ...(import.meta.env.VITE_DEPLOYMENT_MODE !== 'onprem' &&
    user?.tenant?.resellerId !== QSE_ENTERPRISE_ID
      ? [
          {
            id: 'subscription',
            label: 'Manage Subscription',
            icon: CreditCardIcon,
            redirectUrl: AppRoutesList.managementSubscription,
          },
        ]
      : []),
    {
      id: 'profile',
      label: 'Profile',
      icon: UserIcon,
      redirectUrl: AppRoutesList.profile,
    },
  ]

  const securitySubmenuItems: Array<{
    label: string
    redirectUrl: string
  }> = []

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-black">
      <aside className="bg-white dark:bg-[#282828] text-gray-900 dark:text-white">
        <Sidebar
          isOpen={true}
          menuItems={adminMenuItems}
          securitySubmenuItems={securitySubmenuItems}
          setIsOpen={() => {}}
          showSecurityTab={false}
          user={user as User}
        />
      </aside>
      <main className="flex-1 overflow-y-scroll scrollbar-hide bg-gray-50 dark:bg-black relative">
        <Navbar isTenantAdmin={isTenantAdmin} />
        <Outlet />
        {shouldShowQuotaAdjustmentBanner && <QuotaAdjustmentBanner />}
      </main>
    </div>
  )
}

export default DashboardLayout
